import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"

import { isAxiosError } from "axios"

import { axiosInstance } from "@/lib/api-client"
import { API_ROUTES } from "@/lib/api-routes"
import type { Page } from "@/lib/types"
import { filenameFromDisposition, saveBlob } from "@/lib/download"

import type {
  CarrierAccess,
  ListQuote,
  ListsParams,
  SiftOverview,
  SiftUsage,
  CarrierOption,
  CarrierResultRow,
  CarrierSuggestion,
  ListDetail,
  ListInspect,
  ListRead,
} from "../types"

const LISTS_KEY = ["sift", "lists"] as const

/** Rows per page of the lists index. */
export const LISTS_PAGE_SIZE = 25

export const DEFAULT_LISTS_PARAMS: ListsParams = {
  skip: 0,
  limit: LISTS_PAGE_SIZE,
  sort: "-created_at",
}

// ── Lists ───────────────────────────────────────────────────────────

/**
 * One page of the lists index. Status, name search and order are applied
 * server-side; `paged=true` returns `{ items, total, skip, limit }`.
 */
export function siftListsQueryOptions(
  params: ListsParams = DEFAULT_LISTS_PARAMS
) {
  return queryOptions({
    queryKey: [...LISTS_KEY, "index", params],
    queryFn: async () => {
      const res = await axiosInstance.get<Page<ListRead>>(
        API_ROUTES.SIFT.LISTS.LIST,
        {
          params: {
            paged: true,
            skip: params.skip,
            limit: params.limit,
            status: params.status,
            search: params.search || undefined,
            sort: params.sort,
          },
        }
      )
      return res.data
    },
  })
}

export function useSiftListsQuery(params: ListsParams = DEFAULT_LISTS_PARAMS) {
  return useQuery({
    ...siftListsQueryOptions(params),
    // Keep the current page up while the next page or filter loads.
    placeholderData: (prev) => prev,
    // Every row carries `progress_pct`, so the index itself moves the
    // enriching rows along: one poll for the page, not one per row.
    refetchInterval: (query) =>
      query.state.data?.items.some((l) => l.status === "enriching")
        ? 8000
        : false,
  })
}

export function siftListQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...LISTS_KEY, id],
    queryFn: async () => {
      const res = await axiosInstance.get<ListDetail>(
        API_ROUTES.SIFT.LISTS.DETAIL(id)
      )
      return res.data
    },
  })
}

/**
 * One list. While it's enriching the vendor is working through it in the
 * background, so poll for `progress_pct`; stop the moment it settles.
 */
export function useSiftListQuery(id: string) {
  return useQuery({
    ...siftListQueryOptions(id),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      query.state.data?.status === "enriching" ? 4000 : false,
  })
}

/** Reads the CSV's columns + sample rows so the user can map the number. */
export function useInspectFileMutation() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append("file", file)
      const res = await axiosInstance.post<ListInspect>(
        API_ROUTES.SIFT.LISTS.INSPECT,
        form
      )
      return res.data
    },
  })
}

/**
 * Creates the list and returns the cost estimate. Parses and dedupes only —
 * this does NOT dip anything, so it's safe to call before the user commits.
 */
/**
 * Creates the list and BINDS its carrier — the carrier is chosen up front
 * (before upload) and travels with create, not enrich. `carrier` is a
 * required directory value; blank → 422 ("select a carrier before
 * uploading"). Parses and dedupes only; no dip runs here, so it's safe to
 * call before the customer commits to spending.
 */
export function useCreateListMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      file: File
      name: string
      numberColumn: string
      hasHeader: boolean
      carrier: string
    }) => {
      const form = new FormData()
      form.append("file", input.file)
      form.append("name", input.name)
      form.append("number_column", input.numberColumn)
      form.append("has_header", String(input.hasHeader))
      form.append("carrier", input.carrier)
      const res = await axiosInstance.post<ListRead>(
        API_ROUTES.SIFT.LISTS.CREATE,
        form
      )
      return res.data
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: LISTS_KEY }),
  })
}

/**
 * What dipping this list costs now, and whether the balance covers it.
 * Never reused from cache, and refetched whenever the page regains focus
 * — the balance may have changed (a top-up in another tab) since it was
 * shown. For a failed list it covers only the numbers still without a
 * result, which is what a retry charges.
 */
export function useListQuoteQuery(id: string, enabled = true) {
  return useQuery({
    queryKey: [...LISTS_KEY, id, "quote"],
    queryFn: async () => {
      const res = await axiosInstance.get<ListQuote>(
        API_ROUTES.SIFT.LISTS.QUOTE(id)
      )
      return res.data
    },
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: "always",
    // A dip must stay startable if the quote can't be had; the server
    // still refuses a dip the balance can't cover.
    retry: false,
  })
}

/**
 * ⚠ The paid step — the balance is debited when this returns 200. A bare
 * trigger (no body): the carrier was bound at upload. 402 = balance too
 * low, nothing dipped; 409 = already dipping, or nothing left to dip.
 * The charge moves the balance and raises an invoice, so billing and the
 * Sift overview refresh along with the lists.
 */
export function useEnrichListMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosInstance.post<ListRead>(
        API_ROUTES.SIFT.LISTS.ENRICH(id)
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LISTS_KEY })
      void qc.invalidateQueries({ queryKey: ["sift", "overview"] })
      void qc.invalidateQueries({ queryKey: ["billing"] })
    },
  })
}

export function useDeleteListMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(API_ROUTES.SIFT.LISTS.DELETE(id))
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: LISTS_KEY }),
  })
}

/**
 * The deliverable — the list's numbers on its locked carrier, paginated.
 *
 * No carrier param: it's read from the list server-side (the list is
 * locked to one carrier at enrich time), and the endpoint ignores a
 * `carrier` query anyway — there's no client-side carrier switching. Only
 * call on an enriched list; a not-yet-enriched one 400s ("no carrier
 * selected").
 */
export function useCarrierResultsQuery(
  id: string,
  page: { skip: number; limit: number },
  /** Off until the list is enriched — the endpoint 400s before that. */
  ready = true
) {
  return useQuery({
    queryKey: [...LISTS_KEY, id, "results", page],
    queryFn: async () => {
      const res = await axiosInstance.get<Page<CarrierResultRow>>(
        API_ROUTES.SIFT.LISTS.RESULTS(id),
        { params: { skip: page.skip, limit: page.limit } }
      )
      return res.data
    },
    enabled: Boolean(id) && ready,
    // Keep the current page visible while the next loads, so paging doesn't
    // flash empty.
    placeholderData: (prev) => prev,
  })
}

/**
 * The carrier picker's browse list — the full directory, paged and
 * infinite-scrolled. Ranked common-first server-side; render in order,
 * don't re-sort. This is the empty-search path; typing switches to the
 * capped, non-paginated `/carriers/suggest`.
 *
 * `getNextPageParam` returns the next `skip` until the running count
 * reaches `total`, then `undefined` to stop.
 */
export function useCarriersInfiniteQuery(limit = 50) {
  return useInfiniteQuery({
    queryKey: ["sift", "carriers", "browse", limit],
    queryFn: async ({ pageParam }) => {
      const res = await axiosInstance.get<Page<CarrierOption>>(
        API_ROUTES.SIFT.CARRIERS.LIST,
        { params: { search: "", skip: pageParam, limit } }
      )
      return res.data
    },
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const loaded = last.skip + last.items.length
      return loaded < last.total ? loaded : undefined
    },
  })
}

/**
 * Export the list's numbers (on its locked carrier) as CSV.
 *
 * A GET with no carrier param — the carrier is the list's own. Pulled as a
 * blob rather than `window.open` so a failed/expired session surfaces as
 * an error instead of navigating the tab to an error page. Saved under
 * the server's `Content-Disposition` filename; the list name is only the
 * fallback.
 */
export function useExportResultsMutation() {
  return useMutation({
    mutationFn: async (input: { id: string; name: string }) => {
      const res = await axiosInstance.get<Blob>(
        API_ROUTES.SIFT.LISTS.EXPORT(input.id),
        { responseType: "blob" }
      )
      saveBlob(
        res.data,
        filenameFromDisposition(
          res.headers["content-disposition"],
          `${input.name || "scrub"}.csv`
        )
      )
    },
  })
}

// ── Overview + usage ────────────────────────────────────────────────

export function siftOverviewQueryOptions() {
  return queryOptions({
    queryKey: ["sift", "overview"],
    queryFn: async () => {
      const res = await axiosInstance.get<SiftOverview>(
        API_ROUTES.SIFT.OVERVIEW
      )
      return res.data
    },
  })
}

export function useSiftOverviewQuery() {
  return useQuery(siftOverviewQueryOptions())
}

/**
 * Lists per page of the usage by-list table — the API's maximum, so a
 * range's lists (and so its cost, and every bar's lists) arrive at once.
 */
export const USAGE_BY_LIST_PAGE_SIZE = 200

/**
 * `days` is 1–90; the server zero-fills `daily` across the whole range.
 * `skip` pages `by_list`; `by_list_total` is the full count.
 */
export function useSiftUsageQuery(days = 7, skip = 0) {
  return useQuery({
    queryKey: ["sift", "usage", days, skip],
    queryFn: async () => {
      const res = await axiosInstance.get<SiftUsage>(API_ROUTES.SIFT.USAGE, {
        params: { days, skip, limit: USAGE_BY_LIST_PAGE_SIZE },
      })
      return res.data
    },
    // Keeps the current chart up while switching range, so the panel
    // updates rather than blanking.
    placeholderData: (prev) => prev,
  })
}

// ── Carrier access ──────────────────────────────────────────────────
export const CARRIER_ACCESS_KEY = ["sift", "carrier-access"] as const

/**
 * Whether this customer is limited to chosen carriers, and which. Read
 * fresh each time the upload screen opens — an admin can change it at any
 * moment, and a stale list is what produces a refused upload.
 */
export function useCarrierAccessQuery() {
  return useQuery({
    queryKey: CARRIER_ACCESS_KEY,
    queryFn: async (): Promise<CarrierAccess> => {
      try {
        const res = await axiosInstance.get<CarrierAccess>(
          API_ROUTES.SIFT.CARRIER_ACCESS
        )
        return res.data
      } catch (err) {
        // A backend without the endpoint has no restrictions to report.
        if (isAxiosError(err) && err.response?.status === 404) {
          return { restricted: false, carriers: [] }
        }
        throw err
      }
    },
    staleTime: 0,
    refetchOnMount: "always",
  })
}

// ── Carrier autocomplete ────────────────────────────────────────────

/**
 * Typeahead for the filter's free-text carrier box.
 *
 * Degrades to SILENCE, not an error: it's an enhancement on a box that
 * works without it, so a failure (including the directory backend not
 * being deployed yet) resolves to `[]` rather than surfacing anything.
 * Debounce upstream and pass the settled query; it only runs once `q` is
 * non-empty. Results are PRE-RANKED server-side — no `select`, no re-sort.
 */
export function useCarrierSuggestions(q: string, limit = 20) {
  const query = q.trim()
  return useQuery({
    queryKey: ["sift", "carriers", "suggest", query, limit],
    queryFn: async () => {
      try {
        const res = await axiosInstance.get<CarrierSuggestion[]>(
          API_ROUTES.SIFT.CARRIERS.SUGGEST,
          { params: { q: query, limit } }
        )
        return res.data
      } catch {
        return []
      }
    },
    enabled: query.length >= 1,
    placeholderData: (prev) => prev,
    staleTime: 60_000,
    retry: false,
  })
}
